import type {
  BulkActionStartInput,
  BulkActionStartOutput,
  BulkActionStatusOutput,
} from '@charitystack/payments-contracts';
import { getAuthToken } from './auth';

const NOTIFICATION_URL = import.meta.env.VITE_APP_NOTIFICATION_URL;

export type StartBulkActionParams = BulkActionStartInput;
export type StartBulkActionResponse = BulkActionStartOutput;

/**
 * `stale: true` means the backend derived FAILED because the job stopped
 * reporting progress rather than reading a terminal state from storage.
 */
export type BulkActionStatusResponse = Omit<BulkActionStatusOutput, 'status' | 'actionType'> & {
  status: NonNullable<BulkActionStatusOutput['status']>;
  actionType: NonNullable<BulkActionStatusOutput['actionType']>;
};

export async function startBulkAction(
  params: StartBulkActionParams,
): Promise<StartBulkActionResponse> {
  const response = await fetch(`${NOTIFICATION_URL}/bulk-action/start`, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start bulk action' }));
    throw new Error(error.error || `Bulk action start failed: ${response.status}`);
  }

  return response.json();
}

export async function checkBulkActionStatus(jobId: string): Promise<BulkActionStatusResponse> {
  const response = await fetch(`${NOTIFICATION_URL}/bulk-action/status/${jobId}`, {
    method: 'GET',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to check bulk action status' }));
    throw new Error(error.error || `Status check failed: ${response.status}`);
  }

  // The read contract preserves legacy nulls for malformed rows, while the
  // active dashboard call site only continues polling valid job rows.
  return response.json();
}
